import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Upsell } from '../../components/plan/Upsell';
import { usePlan } from '../../components/plan/usePlan';
import type { CommunityTemplateGalleryRead, CommunityZones } from '../../types/api';
import { CommunityDesignPreview } from './CommunityDesignPreview';
import {
  COMMUNITY_TEMPLATES_PATH,
  COMMUNITY_TEMPLATE_NEW_PATH,
  communityBackdropUrl,
} from './communityTemplatePrefill';
import { NEUTRAL_PALETTE, type BrandPalette } from './communityZones';

/** What the template places on its design, named in the order it reads. */
function placesLine(zones: CommunityZones): string {
  const layers =
    zones.v === 1
      ? ['Caption', 'logo']
      : [
          zones.texts.length === 1 ? '1 text box' : `${zones.texts.length} text boxes`,
          ...(zones.logo.enabled ? ['logo'] : []),
        ];
  if (zones.portrait) layers.push('portrait');
  if (zones.value) layers.push('value');
  return layers.join(', ');
}

export interface CommunityGalleryProps {
  templates: readonly CommunityTemplateGalleryRead[];
  isLoading: boolean;
  error: string | null;
  /** Starts the brief on this template — the tile's one action. */
  onUse: (template: CommunityTemplateGalleryRead) => void;
  /** The viewer's own brand colours, so a brand-role design previews as theirs. */
  palette?: BrandPalette;
  /** The viewer's own templates, drawn between the head and the shared grid. */
  yours?: ReactNode;
  /**
   * True when the viewer has templates of their own: the shared grid then gets
   * a heading, so the two lists read as two lists, and its own empty wording.
   */
  hasOwn?: boolean;
}

/**
 * The community gallery: designs members made, approved and shared with every
 * other business on the platform.
 *
 * A tile promises two things and no more — the design, and where the dynamic
 * layers land on it — because that is the whole contract. Everything else about
 * the post (the words, the platform, the vibe) is still the brief's.
 */
export function CommunityGallery({
  templates,
  isLoading,
  error,
  onUse,
  palette = NEUTRAL_PALETTE,
  yours = null,
  hasOwn = false,
}: CommunityGalleryProps) {
  // Making a template is Private templates; using a shared one is open to all.
  const { can } = usePlan();
  const canPrivate = can('private_templates');

  return (
    <div className="pack-gallery">
      <div className="community-gallery__head">
        <p className="kb-section__lede">
          Your own designs, and designs other members made and shared. Pick one and your caption,
          your logo and your photo are placed on it — the design itself is not redrawn.
        </p>
        <div className="community-gallery__actions">
          <Link className="button button--ghost" to={COMMUNITY_TEMPLATES_PATH}>
            My templates
          </Link>
          {canPrivate ? (
            <Link className="button" to={COMMUNITY_TEMPLATE_NEW_PATH}>
              Create template
            </Link>
          ) : null}
        </div>
      </div>

      {canPrivate ? null : <Upsell feature="private_templates" />}

      {yours}

      {hasOwn ? <h2 className="community-yours__title">Shared by members</h2> : null}

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      {isLoading && templates.length === 0 ? (
        <p className="kb-section__status" role="status">
          Loading the community gallery
        </p>
      ) : templates.length === 0 && hasOwn ? (
        <p className="kb-section__status">
          No other member has shared a template yet. Submit one of yours for review and it can be
          the first.
        </p>
      ) : templates.length === 0 ? (
        <div className="kb-section__empty">
          <p className="kb-section__empty-text">
            No community templates yet. Publish the first one: upload a design you made, say where
            the caption and the logo go, and every member can post on it.
          </p>
          {canPrivate ? (
            <Link className="button button--primary" to={COMMUNITY_TEMPLATE_NEW_PATH}>
              Create a template
            </Link>
          ) : null}
        </div>
      ) : (
        <ul className="pack-grid">
          {templates.map((template) => (
            <li className="pack-card" key={template.id}>
              <CommunityDesignPreview
                className="design-preview--tile"
                zones={template.zones}
                imageUrl={communityBackdropUrl(template)}
                palette={palette}
                label={`${template.title} design, in your brand colours`}
              />
              <span className="pack-card__name">{template.title}</span>
              <span className="pack-card__description">{template.description}</span>
              <span className="pack-card__meta">
                {placesLine(template.zones)}
                <span className="community-card__uses">
                  {template.use_count === 1 ? 'Used once' : `Used ${template.use_count} times`}
                </span>
              </span>
              <div className="pack-card__actions">
                <button className="button" type="button" onClick={() => onUse(template)}>
                  Use template
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
